import type { Patient, UpdatePatientRequest, CreateAddressRequest, Medication } from '../types/api';

/**
 * Compare two arrays of medications to check if they're different
 */
const areAllergiesEqual = (original: Medication[], current: Medication[]): boolean => {
  if (original.length !== current.length) return false;
  
  const originalIds = original.map(med => med.id).sort();
  const currentIds = current.map(med => med.id).sort();
  
  return originalIds.every((id, index) => id === currentIds[index]);
};

/**
 * Utility to detect which fields have changed between original patient data and form data
 * Returns only the changed fields to be sent in a PATCH request
 */
export const getChangedFields = (
  originalPatient: Patient,
  formData: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    dob: string;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    address_lines: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
  },
  selectedAllergies?: Medication[]
): { patientChanges: Partial<UpdatePatientRequest>; allergyChanges: boolean } => {
  const patientChanges: Partial<UpdatePatientRequest> = {};
  
  // Check allergy changes - handle both HATEOAS and populated allergies
  let originalAllergies: Medication[] = [];
  if (Array.isArray(originalPatient.allergies)) {
    originalAllergies = originalPatient.allergies.map(allergy => allergy.medication);
  }
  // If allergies is a HATEOAS link, we can't compare here - assume no changes for form validation
  const currentAllergies = selectedAllergies || [];
  const allergyChanges = !areAllergiesEqual(originalAllergies, currentAllergies);

  // Check basic fields
  if (formData.first_name !== (originalPatient.first_name || '')) {
    patientChanges.first_name = formData.first_name;
  }

  if (formData.last_name !== (originalPatient.last_name || '')) {
    patientChanges.last_name = formData.last_name;
  }

  if (formData.phone !== (originalPatient.phone || '')) {
    patientChanges.phone = formData.phone || null;
  }

  if (formData.email !== (originalPatient.email || '')) {
    patientChanges.email = formData.email || null;
  }

  if (formData.dob !== (originalPatient.date_of_birth || '')) {
    patientChanges.date_of_birth = formData.dob;
  }

  if (formData.gender !== originalPatient.gender) {
    patientChanges.gender = formData.gender;
  }

  // Check address fields
  const originalAddress = originalPatient.address || {};
  let hasAddressChanges = false;

  // Compare with joined street array from original data
  const originalStreet = originalAddress.street?.join(', ') || '';
  if (formData.address_lines !== originalStreet) {
    hasAddressChanges = true;
  }

  if (formData.address_city !== (originalAddress.city || '')) {
    hasAddressChanges = true;
  }

  if (formData.address_state !== (originalAddress.state || '')) {
    hasAddressChanges = true;
  }

  if (formData.address_postal_code !== (originalAddress.postal_code || '')) {
    hasAddressChanges = true;
  }

  // Only include address in changes if any address field changed
  if (hasAddressChanges) {
    // Convert form data to API format
    // API expects street as array and postal_code (snake_case)
    patientChanges.address = {
      street: [formData.address_lines], // Convert string back to array
      city: formData.address_city,
      state: formData.address_state,
      postal_code: formData.address_postal_code // snake_case for API
    };
  }

  // Note: Allergies are NOT included in PATCH requests for EditPatientModal
  // They are handled separately via real-time POST/DELETE API calls
  // Only CreatePatientModal should include allergies in the initial creation

  return { patientChanges, allergyChanges };
};

/**
 * Check if there are any changes between original and form data
 * Note: Excludes allergy changes since they're handled real-time via separate API calls
 */
export const hasChanges = (
  originalPatient: Patient,
  formData: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    dob: string;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    address_lines: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
  },
  selectedAllergies?: Medication[]
): boolean => {
  const { patientChanges } = getChangedFields(originalPatient, formData, selectedAllergies);
  // Only check patientChanges, not allergyChanges (allergies handled real-time)
  return Object.keys(patientChanges).length > 0;
};

/**
 * Get allergy changes for API calls
 */
export const getAllergyChanges = (
  originalPatient: Patient,
  selectedAllergies: Medication[]
): { toAdd: Medication[]; toRemove: string[] } => {
  // Handle both HATEOAS and populated allergies
  let originalAllergies: Medication[] = [];
  if (Array.isArray(originalPatient.allergies)) {
    originalAllergies = originalPatient.allergies.map(allergy => allergy.medication);
  }
  const originalIds = originalAllergies.map(med => med.id);
  const currentIds = selectedAllergies.map(med => med.id);
  
  const toAdd = selectedAllergies.filter(med => !originalIds.includes(med.id));
  const toRemove = originalAllergies
    .filter(med => !currentIds.includes(med.id))
    .map(med => med.id);
  
  return { toAdd, toRemove };
};

/**
 * Backward compatibility function that returns only patient changes
 */
export const getChangedFieldsLegacy = (
  originalPatient: Patient,
  formData: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    dob: string;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    address_lines: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
  }
): Partial<UpdatePatientRequest> => {
  const { patientChanges } = getChangedFields(originalPatient, formData);
  return patientChanges;
};